/* =====================================================================
   The driver bundle's HOST CONTRACT (G.3).

   The driver feature is bundled on its own, but it does not stand alone yet: it still reads a set of
   symbols from whatever page it is loaded into. This file is the list, in one place, and it checks
   the list at load time.

   Why a check and not imports. Several of these are top-level `const` in a classic script —
   `STATE`, `HUBS`, `CUSTOMERS`, `fmt` — which means they live in the global LEXICAL environment, not
   on `window`. An unqualified reference from inside the bundle resolves to them correctly and stays
   live, which is exactly what the driver needs (`ACTIVE` is reassigned as the user navigates, so a
   captured copy would be stale). But that same mechanism is invisible: if a host forgets to load
   `geo.js`, the failure is `HUBS is not defined` thrown from somewhere deep in a render, on a
   handheld, in a truck. So the reads stay unqualified and the DEPENDENCY becomes explicit here,
   verified once, loudly, at load.

   Keeping this list honest is the point. If the driver starts using something new, the check fails
   in development the first time it loads — which is the only place a missing dependency is cheap.
   ===================================================================== */

/**
 * What the driver needs from its host, grouped by which file supplies it. The groups are the
 * actionable part of a failure message: "load geo.js" is a fix, "HUBS is undefined" is a puzzle.
 */
// i18n-exempt-start: developer-facing contract and its failure message (never shown to a driver)
const CONTRACT = [
  ['state.js', 'the STATE spine and its mutators', [
    'STATE',              // the single source of truth
    'commit',             // every mutation goes through it (undo stack + persist + broadcast)
    'pushFeed',           // append to the alert feed
    'designationFor',     // stop designation vocabulary
    'reorderRouteStop',   // re-sequencing, used by the on-truck reoptimise
  ]],
  ['time.js', 'the company-zone, 12-hour time formatter (never UTC on screen)', [
    'fmtTime', 'fmtMin', 'fmtWin', 'fmtClockText', 'fmtDate', 'fmtDateTime', 'fmtAny',
  ]],
  ['geo.js', 'hubs, geofencing and the seeded RNG', [
    'HUBS', '_rng', 'areaForPoint',
    'geoPolyline', 'geoStraight',   // the driver's route line: stop order, drawn dashed (never read as the road)
  ]],
  ['screens/00-shared.js', 'shared render helpers', [
    'fmt', 'table', 'toast', 'emptyState', '_coName',
    'escHtml',            // stop names and addresses are user text and the cards are innerHTML
  ]],
  /* NOTE: drvGet (driversync.js) is deliberately NOT listed here.

     It is the bundle's way to GET from /api/driver/*, and the site-history banner uses it - but that
     banner is ADDITIVE. Declaring it made a missing drvGet fail this check, which refuses to render
     the driver app at all: an optional read turned into a boot failure, and a driver with no history
     screen would have had no handheld either. Every use is guarded with typeof at the call site and
     draws nothing when it is absent.

     The list is for what the bundle CANNOT RUN without. Adding something merely useful to it makes
     the check less trustworthy, not more. */
  ['screens/02-radar.js', 'the day navigator and per-day actuals', [
    'dayMeta', 'dayActuals', '_drvStopTs',
  ]],
  ['screens/03-map-engine.js', 'the shared self-hosted vector basemap factory', [
    'baseLayer',
    // The driver map registers itself so the basemap still arrives if it was built while the tile
    // probe was in flight (#121). It was used without being declared here, which is the exact
    // staleness this list exists to prevent: rename it and the handheld loses its map in a truck.
    'attachBase',
  ]],
  ['screens/07-loading.js', 'the load planner the driver reads and scans against', [
    'loadNeeds', 'loadPallets', 'loadAssets', 'bindLoadAsset',
    'drvQrScan', 'drvScanTruckQR', '_drvClock', 'drvClockMark', '_drvRollTime',
    'loadCountsHtml',     // the actual-count editor on the load summary (#91)
    'confirmEmptyLoad',   // asks before an all-zero load is recorded (#91)
  ]],
  ['screens/17-customers.js', 'the customer display list', [
    'CUSTOMERS',
  ]],
  ['app.js', 'navigation and which screen is showing', [
    'go', 'ACTIVE',
  ]],
  ['Leaflet', 'the stop map', [
    'L',
  ]],
];

/**
 * `typeof` by NAME, via an indirect eval.
 *
 * A direct `typeof STATE` would be fine too, but writing thirty of them means the list of
 * dependencies exists twice — once as data for the message and once as code — and the two drift.
 * This keeps one list. Indirect eval runs in global scope, which is where these bindings live.
 */
const globalEval = eval;
const present = (name) => {
  try {
    return globalEval(`typeof ${name}`) !== 'undefined';
  } catch (e) {
    return false;
  }
};

/**
 * Throws if the host is missing anything, naming the file to load. Called once, before any of the
 * driver's own code runs.
 */
export function assertHost() {
  const missing = CONTRACT
    .map(([file, why, names]) => [file, why, names.filter((n) => !present(n))])
    .filter(([, , names]) => names.length > 0);

  if (!missing.length) return;

  const detail = missing
    .map(([file, why, names]) => `  • ${file} (${why}) — missing ${names.join(', ')}`)
    .join('\n');

  throw new Error(
    'The driver bundle cannot run: its host is missing dependencies.\n' + detail +
    '\nSee tower/driver/src/host.js for the full contract.');
}

// i18n-exempt-end
/** The contract, for tests and for anything that wants to render it. */
export const hostContract = CONTRACT;
